import { createFileRoute } from "@tanstack/react-router";
import Index from "@/pages/Index";
import CarmaxHome from "@/components/carmax/CarmaxHome";
import { useThemeLayout } from "@/hooks/useThemeLayout";
import { heroContentQueryOptions } from "@/hooks/useHeroContent";
import { homepageHeroDesktopPreload, homepageHeroMobilePreload, modernHomepageHeroPreload } from "@/lib/heroPreload";
import { HOME_AUTODEALER_JSONLD, HOME_WEBSITE_JSONLD } from "@/lib/home-jsonld";
import { getDeviceHint } from "@/lib/device-hint.functions";

export const Route = createFileRoute("/")({
  loader: async ({ context }) => {
    // Only during SSR: lets the homepage render ONE hero tree instead of
    // server-rendering + hydrating both the mobile and desktop branches.
    // On client navigations device stays null and the CSS fallback applies.
    // Resolve it alongside CMS content instead of adding a second sequential
    // wait to the homepage response path.
    const [hero, device] = await Promise.all([
      context.queryClient.ensureQueryData(heroContentQueryOptions("home", "hero_slides")),
      typeof window === "undefined" ? getDeviceHint() : Promise.resolve(null),
    ]);
    return { hero, device };
  },
  head: ({ loaderData, matches }) => ({
    meta: (() => {
      const rootData = matches.find((match) => String(match.routeId) === "__root__")?.loaderData as { initialTheme?: { layout?: string } | null } | undefined;
      const sources = rootData?.initialTheme?.layout === "carmax" ? modernHomepageHeroPreload(loaderData?.hero) : homepageHeroDesktopPreload(loaderData?.hero);
      const image = sources && ("desktop" in sources ? sources.desktop : sources.href);
      return [
      { title: "Rex & Sons RVs | RV Dealer Castle Hayne NC" },
      { name: "description", content: "Family-owned RV dealer in Castle Hayne, NC since 1997. Shop new and used RVs, service, parts, rentals and financing." },
      { property: "og:title", content: "Rex & Sons RVs | RV Dealer Castle Hayne NC" },
      { property: "og:description", content: "Family-owned RV dealer serving Castle Hayne and Wilmington with RV sales, service, parts and rentals." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      ...(image?.startsWith("https://") ? [{ property: "og:image", content: image }, { name: "twitter:image", content: image }] : []),
      ];
    })(),
    // Homepage JSON-LD (AutoDealer + WebSite). Previously emitted through a
    // duplicate <Helmet> block in src/pages/Index.tsx, which also shipped a
    // second canonical and forced a client-side head diff during hydration.
    scripts: [
      { type: "application/ld+json", children: HOME_AUTODEALER_JSONLD },
      { type: "application/ld+json", children: HOME_WEBSITE_JSONLD },
    ],
    // Self-canonical (the sitewide root canonical was removed; it duplicated
    // the homepage URL onto every page).
    // SSR desktop-hero preload: resolves the LCP image directly from the
    // loader's CMS data so the browser starts the image fetch with the HTML
    // instead of waiting on a client-side page_content request. The
    // HERO_PRELOAD_SCRIPT in __root skips its own fetch when this link exists.
    links: (() => {
      const canonical = { rel: "canonical", href: "https://www.rexandsonsrvs.com/" };
      const rootData = matches.find((match) => String(match.routeId) === "__root__")?.loaderData as { initialTheme?: { layout?: string } | null } | undefined;
      const modern = rootData?.initialTheme?.layout === "carmax";
      const modernSources = modern ? modernHomepageHeroPreload(loaderData?.hero) : null;
      const preload = modern
        ? modernSources && { href: modernSources.desktop, imageSrcSet: undefined, imageSizes: undefined }
        : homepageHeroDesktopPreload(loaderData?.hero);
      const mobile = modern ? modernSources?.mobile : homepageHeroMobilePreload(loaderData?.hero);
      const links: Record<string, unknown>[] = [canonical];
      if (mobile) {
        // Mobile LCP: the strip's <img> is client-rendered, so without this the
        // browser can't discover it until React hydrates.
        links.push({
          rel: "preload",
          as: "image",
          href: mobile,
          media: "(max-width: 767px)",
          fetchPriority: "high",
          "data-hero-preload": "mobile",
        });
      }
      if (preload) {
        links.push({
          rel: "preload",
          as: "image",
          href: preload.href,
          imageSrcSet: preload.imageSrcSet,
          imageSizes: preload.imageSizes,
          media: "(min-width: 768px)",
          fetchPriority: "high",
          "data-hero-preload": "desktop",
        });
      }
      return links;
    })(),
  }),
  component: HomeRoute,
});

// Renders Classic by default; swaps to the theme's layout (admin preview or
// an activated theme) after mount. SSR always serves Classic.
function HomeRoute() {
  const layout = useThemeLayout();
  return layout === "carmax" ? <CarmaxHome /> : <Index />;
}
